import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, http } from '@/shared/api/client';
import { passbookIdsOf } from '@/shared/lib/passbook-access';
import { clearLastPassbookId } from '@/shared/lib/last-passbook';
import type { LoginPayload, PanelUser, SessionResponse } from '@/shared/types';

export type AuthSource = SessionResponse['authSource'];

/** Infere authSource quando o BFF ainda não envia o campo (deploy parcial). */
function inferAuthSource(data: SessionResponse | null | undefined): AuthSource | null {
  if (!data?.user) return null;
  if (data.authSource) return data.authSource;
  if (data.user.keycloak_id) return 'v2';
  return 'legacy';
}

/** Destino seguro pós-login — evita mandar v2 para CRM ou legacy para NPS. */
export function resolvePostLoginPath(
  session: Pick<SessionResponse, 'user' | 'authSource' | 'start'>,
  redirectUrl?: string | null,
): string {
  const authSource = inferAuthSource(session as SessionResponse) ?? 'legacy';
  const home = session.start ?? resolveHomePath(session.user, authSource);

  if (!redirectUrl?.startsWith('/')) return home;

  const toNps = redirectUrl === '/nps' || redirectUrl.startsWith('/nps/');
  if (authSource === 'v2' && !toNps) return home;
  if (authSource === 'legacy' && toNps) return home;
  return redirectUrl;
}

/** Atalho para usuário já autenticado na tela de login. */
export function resolveAuthenticatedRedirect(
  user: PanelUser,
  authSource: AuthSource | null,
  startPath: string | null,
  redirectUrl?: string | null,
): string {
  const inferred = authSource ?? (user.keycloak_id ? 'v2' : 'legacy');
  return resolvePostLoginPath(
    { user, authSource: inferred, start: startPath ?? resolveHomePath(user, inferred) },
    redirectUrl,
  );
}

interface AuthContextValue {
  /** Usuário em vigor na tela: o da sessão, ou o que o master está "vendo como". */
  user: PanelUser | null;
  /** Usuário da sessão de verdade. As chamadas à API saem sempre dele. */
  realUser: PanelUser | null;
  /** Usuário que o master escolheu ver como; `null` fora desse modo. */
  viewAs: PanelUser | null;
  startViewAs: (target: PanelUser) => void;
  stopViewAs: () => void;
  authSource: AuthSource | null;
  startPath: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<SessionResponse>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const ME_KEY = ['auth', 'me'] as const;
const VIEW_AS_KEY = 'fixe:viewAs';

function readViewAs(): PanelUser | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY);
    return raw ? (JSON.parse(raw) as PanelUser) : null;
  } catch {
    return null;
  }
}

function writeViewAs(target: PanelUser | null): void {
  try {
    if (target) sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(target));
    else sessionStorage.removeItem(VIEW_AS_KEY);
  } catch {
    /* storage indisponível */
  }
}

async function fetchMe(): Promise<SessionResponse | null> {
  try {
    return await http.get<SessionResponse>('/bff/auth/me');
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) return null;
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // "Ver como": só muda o que a tela decide (menu, guards, páginas). A sessão
  // continua a do master, então os dados carregam como master.
  const [viewAs, setViewAs] = useState<PanelUser | null>(readViewAs);
  const startViewAs = useCallback((target: PanelUser) => {
    writeViewAs(target);
    setViewAs(target);
  }, []);
  const stopViewAs = useCallback(() => {
    writeViewAs(null);
    setViewAs(null);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    staleTime: Infinity,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => http.post<SessionResponse>('/bff/auth/login', payload),
    onSuccess: (session) => {
      queryClient.setQueryData(ME_KEY, session);
      // O /me é a leitura que aplica as páginas do usuário. Se o login vier
      // sem elas, esta busca troca o menu sem esperar um refresh manual.
      void queryClient.fetchQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 0 });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => http.post<void>('/bff/auth/logout'),
    onSettled: () => {
      queryClient.setQueryData(ME_KEY, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
      // Deslogar encerra a inspeção e o "ver como".
      clearLastPassbookId();
      writeViewAs(null);
      setViewAs(null);
    },
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data?.user ? (viewAs ?? data.user) : null,
      realUser: data?.user ?? null,
      viewAs: data?.user ? viewAs : null,
      startViewAs,
      stopViewAs,
      authSource: inferAuthSource(data),
      // No "ver como" a rota inicial é a do usuário simulado. Com a do master
      // (/clientes), guard de Admin → /login → /clientes → /login, sem fim.
      startPath: data?.user && viewAs ? null : (data?.start ?? null),
      isLoading,
      isAuthenticated: Boolean(data?.user),
      login: (payload) => loginMutation.mutateAsync(payload),
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
    }),
    [data, isLoading, loginMutation, logoutMutation, viewAs, startViewAs, stopViewAs],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}

/** Rota inicial conforme authSource e role (espelha o BFF). */
// eslint-disable-next-line react-refresh/only-export-components
export function resolveHomePath(user: PanelUser, authSource?: AuthSource | null): string {
  if (authSource === 'v2') return '/nps';

  if (user.role === 'Admin') return '/clientes';
  if (user.role === 'Owner') return '/owner';
  if (user.role === 'Nps') return '/nps';

  // Conta de grupo (mais de uma marca) escolhe a loja antes de entrar.
  const storeIds = passbookIdsOf(user);
  if (storeIds.length !== 1) return '/selecionar-loja';
  const storeId = storeIds[0];

  if (user.role === 'Validator') return `/loja/${storeId}/operador`;
  if (user.role === 'Campaign') return `/loja/${storeId}/campanhas/automatizadas`;
  if (user.role === 'Operator') return `/loja/${storeId}/leads`;
  if (user.role === 'Manager') return `/loja/${storeId}`;
  if (user.role === 'Employee') return '/selecionar-loja';

  return `/loja/${storeId}`;
}
